import React, { useState, useRef, useEffect, useCallback, memo } from 'react';
import { createPortal } from 'react-dom';
import { useGroupStore } from '@/stores/groupStore';
import { GroupColorTheme } from '@/types';
import { COLOR_OPTIONS } from '@/config/groupThemes';
import {
  Play,
  LayoutGrid,
  FolderPlus,
  Film,
  Ungroup,
  Download,
  Palette,
} from 'lucide-react';

interface GroupToolbarProps {
  groupId: string;
  style?: React.CSSProperties;
}

interface ToolbarButton {
  id: string;
  type: 'button';
  icon: React.ElementType;
  label: string;
  onClick: () => void;
}

interface ToolbarSeparator {
  id: string;
  type: 'separator';
}

type ToolbarItem = ToolbarButton | ToolbarSeparator;

const useDropdown = () => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const updatePos = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left });
    }
  }, []);

  useEffect(() => {
    if (open) updatePos();
  }, [open, updatePos]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
        triggerRef.current && !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return { open, setOpen, triggerRef, dropdownRef, pos };
};

export const GroupToolbar: React.FC<GroupToolbarProps> = memo(({ groupId, style }) => {
  const setGroupColor = useGroupStore((s) => s.setGroupColor);
  const autoArrangeGroup = useGroupStore((s) => s.autoArrangeGroup);
  const executeGroup = useGroupStore((s) => s.executeGroup);
  const convertToStoryboard = useGroupStore((s) => s.convertToStoryboard);
  const dissolveGroup = useGroupStore((s) => s.dissolveGroup);
  const addGroupToToolbox = useGroupStore((s) => s.addGroupToToolbox);
  const batchDownloadGroup = useGroupStore((s) => s.batchDownloadGroup);

  const colorDropdown = useDropdown();

  const handleColorSelect = (color: GroupColorTheme) => {
    setGroupColor(groupId, color);
    colorDropdown.setOpen(false);
  };

  const items: ToolbarItem[] = [
    { id: 'sep-color', type: 'separator' },
    { id: 'arrange', type: 'button', icon: LayoutGrid, label: '排列', onClick: () => autoArrangeGroup(groupId) },
    { id: 'execute', type: 'button', icon: Play, label: '整组执行', onClick: () => executeGroup(groupId) },
    { id: 'toolbox', type: 'button', icon: FolderPlus, label: '添加到工具箱', onClick: () => addGroupToToolbox(groupId) },
    { id: 'storyboard', type: 'button', icon: Film, label: '转分镜组', onClick: () => convertToStoryboard(groupId) },
    { id: 'dissolve', type: 'button', icon: Ungroup, label: '解组', onClick: () => dissolveGroup(groupId) },
    { id: 'download', type: 'button', icon: Download, label: '批量下载', onClick: () => batchDownloadGroup(groupId) },
  ];

  return (
    <div
      className="flex items-center gap-1 pointer-events-auto bg-[#1c1c1e]/95 backdrop-blur-xl border border-white/10 rounded-xl px-3 py-1.5 shadow-lg"
      style={style}
    >
      {/* 颜色选择器 - 特殊下拉，不在 items 中 */}
      <div className="relative">
        <button
          ref={colorDropdown.triggerRef}
          onClick={() => colorDropdown.setOpen(!colorDropdown.open)}
          onMouseDown={(e) => e.stopPropagation()}
          className="flex items-center gap-[6px] h-9 px-2.5 rounded-md bg-transparent text-white hover:text-white hover:bg-white/10 transition-all"
          title="选择颜色"
        >
          <Palette size={16} />
        </button>
        {colorDropdown.open && createPortal(
          <div
            ref={colorDropdown.dropdownRef}
            className="fixed bg-[#1c1c1e]/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-lg overflow-hidden z-[9999] p-2"
            style={{ top: colorDropdown.pos.top, left: colorDropdown.pos.left }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="grid grid-cols-3 gap-1.5">
              {COLOR_OPTIONS.map((opt) => (
                <button
                  key={opt.color}
                  onClick={() => handleColorSelect(opt.color)}
                  className="flex flex-col items-center gap-1 px-2 py-1.5 rounded-lg hover:bg-white/10 transition-all"
                  title={opt.label}
                >
                  <div
                    className="w-5 h-5 rounded-full border border-white/20"
                    style={{ background: opt.bg }}
                  />
                  <span className="text-[10px] text-white/60">{opt.label}</span>
                </button>
              ))}
            </div>
          </div>,
          document.body
        )}
      </div>

      {items.map((item) => {
        if (item.type === 'separator') {
          return <div key={item.id} className="w-px h-5 bg-white/10 mx-1" />;
        }

        return (
          <button
            key={item.id}
            onClick={item.onClick}
            onMouseDown={(e) => e.stopPropagation()}
            className="flex items-center gap-[6px] h-9 px-2.5 rounded-md bg-transparent text-white hover:text-white hover:bg-white/10 transition-all"
            title={item.label}
          >
            <item.icon size={16} />
            <span className="text-sm font-medium whitespace-nowrap">{item.label}</span>
          </button>
        );
      })}
    </div>
  );
});

GroupToolbar.displayName = 'GroupToolbar';
